/**
 * Client-Spiegel von `apps/api/src/damage.abrechnung.ts` (Excel-Vorlage
 * „Abrechnung Schaden"): dieselbe Formel, Zeile für Zeile. Der Server bleibt
 * maßgeblich; das hier ist die Live-Anzeige beim Tippen.
 *   Brutto      = Netto · (1 + Steuer/100)
 *   Kosten      = Brutto − Kürzung (je Zeile, nicht unter 0)
 *   Vorsteuer   = (K − K/(1+Steuer/100)) · Anteil/100   (nur bei „Ja")
 *   SB          = fix, sonst Prozent · Zwischensumme    (beides → Konflikt, fix gilt)
 *   Entschäd.   = Zwischensumme − SB;  Offen = Entschädigung − Σ Zahlungen
 */
import type { MappeAbrechnung } from "../types";

export interface AbrechnungParameter {
  versSteuerPct?: number | null;
  vorsteuerabzug?: boolean | null;
  vorsteuerabzugPct?: number | null;
  sbFix?: number | null;
  sbProzent?: number | null;
  claimAmount?: number | null;
}

export interface AbrechnungZeile {
  invoiceNet: number | null | undefined;
  deductionGross: number | null | undefined;
  paymentGross: number | null | undefined;
}

export const STEUER_STANDARD_PCT = 19;

function zahl(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function berechneAbrechnung(p: AbrechnungParameter, rows: AbrechnungZeile[]): MappeAbrechnung {
  const steuerPct = p.versSteuerPct == null ? STEUER_STANDARD_PCT : zahl(p.versSteuerPct);
  const faktor = 1 + steuerPct / 100;

  let invoiceNetSum = 0;
  let invoiceGrossSum = 0;
  let deductionGrossSum = 0;
  let costsGross = 0;
  let alreadyPaid = 0;
  for (const row of rows) {
    const netto = zahl(row.invoiceNet);
    const brutto = netto * faktor;
    const kuerzung = zahl(row.deductionGross);
    invoiceNetSum += netto;
    invoiceGrossSum += brutto;
    deductionGrossSum += kuerzung;
    costsGross += Math.max(0, brutto - kuerzung);
    alreadyPaid += zahl(row.paymentGross);
  }

  const vorsteuerabzug = p.vorsteuerabzug === true;
  const anteil = vorsteuerabzug ? zahl(p.vorsteuerabzugPct) : 0;
  const vorsteuerKonflikt = vorsteuerabzug && anteil <= 0;
  const steuerAnteil = costsGross - costsGross / faktor;
  const vatDeductionAmount = vorsteuerabzug && anteil > 0 ? steuerAnteil * (anteil / 100) : 0;
  const intermediate = Math.max(0, costsGross - vatDeductionAmount);

  const sbFix = zahl(p.sbFix);
  const sbProzent = zahl(p.sbProzent);
  const sbKonflikt = sbFix > 0 && sbProzent > 0;
  const sbAmount = sbFix > 0 ? sbFix : sbProzent > 0 ? intermediate * (sbProzent / 100) : 0;

  const entschaedigung = Math.max(0, intermediate - sbAmount);

  return {
    anspruch: zahl(p.claimAmount),
    // Der Server kennt keine VR-Kürzung (nicht Teil der Formel); Feld nur aus dem Typ.
    kuerzungVR: 0,
    invoiceNetSum,
    invoiceGrossSum,
    deductionGrossSum,
    costsGross,
    steuerPct,
    vorsteuerabzug,
    vatPct: anteil,
    vatDeductionAmount,
    intermediate,
    sbFix,
    sbProzent,
    sbAmount,
    sbKonflikt,
    vorsteuerKonflikt,
    entschaedigung,
    alreadyPaid,
    openPayments: Math.max(0, entschaedigung - alreadyPaid),
  };
}

const EURO = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

/** „1.234,50 €" — geschütztes Leerzeichen der Intl-Ausgabe wird zum normalen. */
export function formatEuro(n: number): string {
  return EURO.format(n).replace(/[  ]/g, " ");
}

/** Deutsche Eingabe → Zahl: „1.250,50" → 1250.5; leer/nicht numerisch → null. */
export function parseBetrag(s: string): number | null {
  const treffer = s.replace(/\s/g, "").match(/-?\d{1,3}(?:\.\d{3})+(?:,\d+)?|-?\d+(?:,\d+)?/);
  if (!treffer) return null;
  const zahlWert = Number(treffer[0].replace(/\./g, "").replace(",", "."));
  return Number.isFinite(zahlWert) ? zahlWert : null;
}

/**
 * Eingabe im Formular → Zahl. Wie `parseBetrag`, verträgt aber zusätzlich den
 * Punkt als Dezimaltrenner („12.5", „1234.56"), wenn kein Komma vorkommt und
 * es kein Tausenderpunkt sein kann. „€" und „%" werden überlesen.
 */
export function parseEingabe(s: string): number | null {
  const roh = s.replace(/[\s€%]/g, "");
  if (!roh) return null;
  if (!roh.includes(",") && /^-?\d+\.\d{1,2}$/.test(roh)) {
    const wert = Number(roh);
    return Number.isFinite(wert) ? wert : null;
  }
  return parseBetrag(roh);
}

const ZAHL_DE = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Betrag fürs Eingabefeld: „1.234,50" ohne Währungszeichen. */
export function formatBetragEingabe(n: number): string {
  return ZAHL_DE.format(n);
}
